import { createFileRoute, redirect } from '@tanstack/react-router';
import type { Practitioner } from '@medplum/fhirtypes';

import { medplum } from '@/config/medplum';
import { queryClient } from '@/config/queryClient';
import { getUserPermissions } from '@/lib/auth/permissions';
import { getAuthUserQueryKey, fetchAuthUser } from '@/features/practitioners/hooks/useMedplumAuth';

export const Route = createFileRoute('/(authenticated)')({
  beforeLoad: async ({ location }) => {
    const activeLogin = medplum.getActiveLogin();

    if (!activeLogin) {
      throw redirect({
        to: '/',
        search: { redirect: location.href },
      });
    }

    if (!medplum.getProfile()) {
      await medplum.getProfileAsync();
    }

    // If profile is still null after fetching, the token was invalid and the session
    // has been cleared. Redirect to login without an error param — this is a session
    // expiry, not an authorisation failure.
    if (!medplum.getProfile()) {
      throw redirect({ to: '/', search: { redirect: location.href } });
    }

    const { permissions } = await getUserPermissions();
    if (permissions.length === 0) {
      await medplum.signOut();
      queryClient.clear();
      throw redirect({ to: '/', search: { error: 'unauthorized' } });
    }

    const profile = medplum.getProfile();
    if (profile?.resourceType === 'Practitioner') {
      await queryClient.ensureQueryData({
        queryKey: getAuthUserQueryKey(profile),
        queryFn: () => fetchAuthUser(medplum, profile as Practitioner),
        staleTime: Number.POSITIVE_INFINITY,
      });
    }
  },
});
